// AudioSource over a RemoteAudioFeed. This is the production source: give it the
// LiveSwitch feed and it produces the 16 kHz mono PCM the pipeline consumes.
//
// It is the last file downstream that has any idea people are on a call, and
// even here that is only "a track appeared, a track left".

import { BaseAudioSource } from '../AudioSource';
import { MonoMixerGraph } from '../MonoMixerGraph';
import type { RemoteAudioFeed } from '../RemoteAudioFeed';
import { debugLog } from '../../debug';

export class RemoteTracksAudioSource extends BaseAudioSource {
  private graph = new MonoMixerGraph();
  private unsubscribes: Array<() => void> = [];
  private started = false;

  constructor(private readonly feed: RemoteAudioFeed) {
    super();
  }

  get activeTrackCount(): number {
    return this.graph.trackCount;
  }

  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    try {
      await this.graph.start();
    } catch (err) {
      this.started = false;
      this.emitError(err instanceof Error ? err : new Error(String(err)));
      throw err;
    }

    this.graph.onFrame((pcm, timestampMs) => this.emitFrame(pcm, timestampMs));

    const existing = this.feed.current();
    debugLog.info('audio source: started with', existing.length, 'remote track(s)');
    for (const entry of existing) this.graph.addTrack(entry.id, entry.track);

    // Late joiners get picked up, leavers get dropped (spec s4).
    this.unsubscribes.push(
      this.feed.onAdded(({ id, track }) => {
        try {
          debugLog.info(
            'audio source: +track',
            id.slice(0, 8),
            `kind=${track.kind}`,
            `enabled=${track.enabled}`,
            `muted=${track.muted}`,
            `state=${track.readyState}`,
          );
          this.graph.addTrack(id, track);
        } catch (err) {
          this.emitError(err instanceof Error ? err : new Error(String(err)));
        }
      }),
      this.feed.onRemoved((id) => {
        debugLog.info('audio source: -track', id.slice(0, 8));
        this.graph.removeTrack(id);
      }),
    );
  }

  async stop(): Promise<void> {
    for (const off of this.unsubscribes) off();
    this.unsubscribes = [];
    await this.graph.stop();
    this.started = false;
  }
}
